// ─────────────────────────────────────────────────────────────────────────────
//  VirtualScrollController
//  A Lit Reactive Controller that provides windowed (virtual) rendering for
//  any large list.  Shared by uwc-virtualscroller, uwc-dropdown, and
//  uwc-datatable.
//
//  Algorithm:
//    • Track the scroll container's scrollTop via a passive scroll listener.
//    • Derive firstVisible / lastVisible indices from scrollTop + containerHeight.
//    • Expand the window by `buffer` items on each side to hide paint latency.
//    • The host renders only visibleItems; paddingBefore / paddingAfter spacers
//      maintain the correct scroll height for the full list.
//    • A ResizeObserver re-derives containerHeight when the host is resized.
//
//  Usage inside a LitElement:
//
//    private _vs = new VirtualScrollController<Item>(this, { itemSize: 48 });
//
//    override firstUpdated() {
//      this._vs.attach(this.shadowRoot!.querySelector('.scroll-container')!);
//    }
//
//    override willUpdate(changed) {
//      if (changed.has('items')) this._vs.setItems(this.items);
//    }
//
//    override render() {
//      return html`
//        <div class="scroll-container" style="overflow:auto;height:400px;">
//          <div style="height:${this._vs.paddingBefore}px" aria-hidden="true"></div>
//          ${this._vs.visibleItems.map(({ item, index }) =>
//            html`<div style="height:${this._vs.itemSize}px">${item.label}</div>`
//          )}
//          <div style="height:${this._vs.paddingAfter}px" aria-hidden="true"></div>
//        </div>
//      `;
//    }
//
// ─────────────────────────────────────────────────────────────────────────────
import type { ReactiveController, ReactiveControllerHost } from 'lit';

// ── Public types ─────────────────────────────────────────────────────────────

/** The inclusive-start, exclusive-end index range currently rendered. */
export interface VirtualRange {
  first: number;
  last:  number;
}

/** An item together with its absolute position in the full list. */
export interface VirtualItem<T> {
  item:  T;
  index: number;
}

export interface VirtualScrollOptions {
  /**
   * Estimated pixel height (or width in horizontal mode) of a single item.
   * Used for spacer calculations.  Default: 48.
   */
  itemSize?: number;

  /**
   * Number of extra items rendered above and below the visible window.
   * Larger values reduce blank-flash during fast scrolling at the cost of
   * more DOM nodes.  Default: 4.
   */
  buffer?: number;

  /**
   * Called whenever the rendered range changes.  Use to fire component events
   * or load more data (infinite scroll).
   */
  onRangeChange?: (range: VirtualRange) => void;
}

// ── Controller ───────────────────────────────────────────────────────────────

export class VirtualScrollController<T = unknown> implements ReactiveController {
  readonly host: ReactiveControllerHost;

  // ── Configuration (mutable after construction) ────────────────────
  private _itemSize: number;
  private _buffer:   number;
  private readonly _onRangeChange: ((r: VirtualRange) => void) | null;

  // ── State ─────────────────────────────────────────────────────────
  private _items:           T[]             = [];
  private _scrollTop:       number          = 0;
  private _containerHeight: number          = 0;
  private _scrollEl:        HTMLElement | null = null;
  private _resizeObs:       ResizeObserver | null = null;
  private _lastRange:       VirtualRange    = { first: 0, last: 0 };

  // ── Construction ─────────────────────────────────────────────────

  constructor(host: ReactiveControllerHost, opts: VirtualScrollOptions = {}) {
    this.host            = host;
    this._itemSize       = opts.itemSize       ?? 48;
    this._buffer         = opts.buffer         ?? 4;
    this._onRangeChange  = opts.onRangeChange  ?? null;
    host.addController(this);
  }

  // ── Reactive controller lifecycle ─────────────────────────────────

  /** Intentionally empty — wiring is deferred to `attach()`. */
  hostConnected(): void { /* noop */ }

  hostDisconnected(): void {
    this.detach();
  }

  // ── Public API ────────────────────────────────────────────────────

  /** Replace the items array and request a host re-render. */
  setItems(items: T[]): void {
    this._items = items;
    // Clamp scrollTop in case the new list is shorter
    if (this._scrollEl) {
      this._scrollTop = Math.min(this._scrollTop, this.totalHeight);
    }
    this.host.requestUpdate();
  }

  /** Update estimated item height and request a re-render. */
  setItemSize(px: number): void {
    this._itemSize = px;
    this.host.requestUpdate();
  }

  /** Update render buffer size. */
  setBuffer(n: number): void {
    this._buffer = n;
    this.host.requestUpdate();
  }

  // ── Derived state (read-only) ─────────────────────────────────────

  get items(): readonly T[]    { return this._items; }
  get totalCount(): number     { return this._items.length; }
  get itemSize(): number       { return this._itemSize; }

  /** Total pixel height the full (un-windowed) list would occupy. */
  get totalHeight(): number    { return this._items.length * this._itemSize; }

  /** The index range currently rendered (first inclusive, last exclusive). */
  get range(): VirtualRange {
    const safeHeight = this._containerHeight > 0 ? this._containerHeight : 400;
    const first = Math.max(
      0,
      Math.floor(this._scrollTop / this._itemSize) - this._buffer,
    );
    const visibleCount = Math.ceil(safeHeight / this._itemSize);
    const last = Math.min(
      this._items.length,
      first + visibleCount + this._buffer * 2,
    );
    return { first, last };
  }

  /** Items currently in the render window with their absolute indices. */
  get visibleItems(): VirtualItem<T>[] {
    const { first, last } = this.range;
    return this._items.slice(first, last).map((item, i) => ({
      item,
      index: first + i,
    }));
  }

  /** Pixel height of the empty spacer placed BEFORE the rendered items. */
  get paddingBefore(): number {
    return this.range.first * this._itemSize;
  }

  /** Pixel height of the empty spacer placed AFTER the rendered items. */
  get paddingAfter(): number {
    return (this._items.length - this.range.last) * this._itemSize;
  }

  /** Current scroll offset of the attached container (px). */
  get scrollTop(): number { return this._scrollTop; }

  // ── Scroll control ────────────────────────────────────────────────

  /**
   * Scroll so that the item at `index` is visible.
   * Defaults to 'auto' (instant) so UI interactions feel immediate;
   * pass 'smooth' for animated transitions.
   */
  scrollToIndex(index: number, behavior: ScrollBehavior = 'auto'): void {
    if (!this._scrollEl) return;
    const top = Math.max(0, Math.min(index * this._itemSize, this.totalHeight));
    this._scrollEl.scrollTo({ top, behavior });
  }

  /** Scroll to the very beginning of the list. */
  scrollToTop(behavior: ScrollBehavior = 'auto'): void {
    this._scrollEl?.scrollTo({ top: 0, behavior });
  }

  /** Scroll to the very end of the list. */
  scrollToBottom(behavior: ScrollBehavior = 'auto'): void {
    this._scrollEl?.scrollTo({ top: this.totalHeight, behavior });
  }

  // ── Container wiring ──────────────────────────────────────────────

  /**
   * Attach the controller to a DOM scroll container.
   *
   * Call from the host's `firstUpdated()` once the shadow DOM is ready.
   * If called multiple times, the previous container is automatically
   * detached first.
   */
  attach(scrollEl: HTMLElement): void {
    this.detach();
    this._scrollEl        = scrollEl;
    this._containerHeight = scrollEl.clientHeight;
    this._scrollTop       = scrollEl.scrollTop;

    scrollEl.addEventListener('scroll', this._onScroll, { passive: true });

    // Track container height changes so the visible window stays accurate
    this._resizeObs = new ResizeObserver(entries => {
      const h = entries[0]?.contentRect.height ?? 0;
      if (h !== this._containerHeight) {
        this._containerHeight = h;
        this.host.requestUpdate();
      }
    });
    this._resizeObs.observe(scrollEl);
  }

  /**
   * Remove scroll listeners and observer.
   * Called automatically when the host disconnects.
   */
  detach(): void {
    if (this._scrollEl) {
      this._scrollEl.removeEventListener('scroll', this._onScroll);
      this._scrollEl = null;
    }
    this._resizeObs?.disconnect();
    this._resizeObs   = null;
    this._scrollTop   = 0;
    this._containerHeight = 0;
  }

  // ── Private ───────────────────────────────────────────────────────

  /** Arrow function preserves `this` for removeEventListener identity. */
  private readonly _onScroll = (): void => {
    if (!this._scrollEl) return;
    this._scrollTop = this._scrollEl.scrollTop;

    const r = this.range;
    const changed = r.first !== this._lastRange.first
                 || r.last  !== this._lastRange.last;

    if (changed) {
      this._lastRange = r;
      this._onRangeChange?.(r);
      // Only request a re-render when the visible window actually shifts.
      // Requesting on every pixel causes Lit to mutate the DOM on every frame,
      // which interacts with scroll-anchoring and produces a runaway scroll loop.
      this.host.requestUpdate();
    }
  };
}
